import { computed, onScopeDispose } from 'vue'
import { useQuery, useMutation } from './api'
import { useAuth } from './useAuth'
import { useAccess } from './useAccess'
import useAuthStore, { isUserToken } from '../stores/authStore'
import { useUserSettingsStore } from '../stores/userSettingsStore'
import { useUserExtendedData } from './useUserExtendedData'
import { useDebounceFn, useEventListener } from '@vueuse/core'
import { deepMerge, deepSet } from 'ts-deep-utils'
import { toRaw } from 'vue'
import type { components } from '../types/_api'

type UserSettings = components['schemas']['User-Settings']
type UserSettingsData = UserSettings['settings']

const FLUSH_DELAY_MS = 5000

export function useUserSettings() {
  const { isAuthenticated } = useAuth()
  const authStore = useAuthStore()
  const userSettingsStore = useUserSettingsStore()
  const userExtendedDataQuery = useUserExtendedData()
  const access = useAccess()

  const userId = computed(() => {
    return isUserToken(authStore.selectedToken?.payload) ? authStore.selectedToken.payload.u_id : null
  })

  const userSettingsQuery = useQuery(
    '/user--settings',
    computed(() => ({
      query: {
        where: {
          user: userId.value,
        },
      },
    })),
    {
      enabled: () => userId.value != null && isAuthenticated.value,
    }
  )

  const createUserSettingsMutation = useMutation('post', '/user--settings', {
    meta: { notifyOnSuccess: false },
  })
  const updateUserSettingsMutation = useMutation('patch', '/user--settings/{resource_id}', {
    meta: { notifyOnSuccess: false },
  })

  const serverUserSettings = computed(() => userSettingsQuery.data.value?._items?.[0] ?? null)

  const settings = computed<UserSettingsData>(() => {
    const raw = toRaw(serverUserSettings.value?.settings ?? { ui: {}, item_component: {} })

    // Clone only when a patch needs to be merged. Callers are read-only,
    // so the no-patch path returns the raw server snapshot — important on
    // the dashboard hot path where `creditor_selection.included` may hold
    // thousands of entries.
    if (userId.value != null) {
      const pendingPatch = userSettingsStore.pendingSettings.get(userId.value)
      if (pendingPatch != null) {
        return deepMerge(structuredClone(raw), pendingPatch)
      }
      const uncommittedPatch = userSettingsStore.getUserUncommittedSettings(userId.value)
      if (uncommittedPatch != null) {
        return deepMerge(structuredClone(raw), uncommittedPatch)
      }
    }

    return raw
  })

  const saveUserSettings = async () => {
    if (userId.value == null) return
    if (!userSettingsStore.hasUserUncommittedSettings(userId.value)) return
    if (!(await access.canWriteAttributeAsync('user--settings', 'settings'))) return

    await userExtendedDataQuery.suspense()

    const currentUserId = userId.value
    const patchBody = userSettingsStore.getUserUncommittedSettings(currentUserId)
    if (patchBody == null) return

    if (userSettingsStore.pendingSettings.has(userId.value)) return
    userSettingsStore.pendingSettings.set(currentUserId, patchBody)
    userSettingsStore.clearUserUncommittedSettings(currentUserId)

    try {
      if (serverUserSettings.value == null) {
        if (!(await access.canCallMethodAsync('post', '/user--settings'))) {
          userSettingsStore.restorePendingUserSettings(currentUserId, patchBody)
          return
        }

        const defaultSettings: UserSettingsData = {
          ui: {
            theme: 'light',
            language: 'sv',
          },
          item_component: {},
        }
        const mergedSettings = deepMerge(defaultSettings, patchBody)

        await createUserSettingsMutation.mutateAsync({
          // @ts-expect-error Swagger lies
          body: {
            partner: userExtendedDataQuery.data.value?.partner,
            account: userExtendedDataQuery.data.value?.account,
            user: currentUserId,
            settings: mergedSettings,
          },
        })
      } else {
        if (!(await access.canCallMethodAsync('patch', '/user--settings/{resource_id}'))) {
          userSettingsStore.restorePendingUserSettings(currentUserId, patchBody)
          return
        }

        await updateUserSettingsMutation.mutateAsync({
          path: { resource_id: serverUserSettings.value._id },
          header: { 'If-Match': serverUserSettings.value._etag },
          body: {
            // @ts-expect-error We can skip a prop and it won't be updated, Swagger lies
            settings: patchBody,
          },
        })
      }
    } catch (error) {
      console.error('Failed to save user settings', error)
      userSettingsStore.restorePendingUserSettings(currentUserId, patchBody)
    } finally {
      userSettingsStore.pendingSettings.delete(currentUserId)
    }
  }
  const queueUserSettingsSave = useDebounceFn(saveUserSettings, FLUSH_DELAY_MS)

  onScopeDispose(() => {
    void saveUserSettings()
  })

  useEventListener(window, 'beforeunload', () => {
    void saveUserSettings()
  })

  // Function to update a user setting using a dotted path
  const updateSetting = (dottedPath: string, value: unknown) => {
    if (userId.value == null) return

    // Build a nested patch object from the dotted path
    const patch: Record<string, unknown> = {}
    deepSet(patch, dottedPath, value)

    userSettingsStore.mergeUserUncommittedSettings(userId.value, patch)
    void queueUserSettingsSave()
  }

  return {
    isLoading: userSettingsQuery.isLoading,
    isPending: computed(() => createUserSettingsMutation.isPending.value || updateUserSettingsMutation.isPending.value),

    settings,
    serverUserSettings,

    updateSetting,
    saveUserSettingsImmediately: saveUserSettings,
  }
}
